import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { SELECTORS } from '../../constants/selectors';
import { STATE_CLASSES } from '../../constants/classes';
import {
  PRICE_SLIDER_MAX,
  buildPriceRangeFilterExpr,
  parsePriceInputValue,
  countActiveFilters,
  getExclusiveCategoryIds,
} from './collection-filter.logic';

/**
 * 絞り込み UI コンポーネント
 *
 * @element kd-collection-filter
 * @description PC サイドバーのアコーディオン開閉、SP モーダルの開閉、
 *              条件タグ表示、クリアボタン、価格スライダーを制御する。
 *              HTML は Liquid が生成し、このコンポーネントは振る舞いのみ担当。
 *              フィルタ適用時は 'kd-collection-filter-apply' CustomEvent を bubble し、
 *              祖先コンポーネントが Algolia 検索を実行する。
 */

/**
 * 属性セレクタ値のエスケープ。ブラウザでは CSS.escape、jsdom（CSS 未実装）ではフォールバックを使う。
 * data-filter-id にコロン等を含む場合でも `input[data-filter-id="..."]` が壊れないようにする。
 */
function escapeAttr(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return value.replace(/["\\]/g, '\\$&');
}

@customElement('kd-collection-filter')
export class KdCollectionFilter extends LitElement {
  createRenderRoot() {
    return this;
  }

  /** アコーディオンのアニメーション時間（ms） */
  private static readonly ACCORDION_DURATION = 200;

  /** 条件タグの CSS クラス（IS 接続時に差し替え容易にするため定数化） */
  private static readonly TAG_CLASSES = 'tw:inline-flex tw:items-center tw:gap-2 tw:px-[10px] tw:py-1 tw:bg-surface-card-sub tw:border tw:border-border-subdued tw:rounded-[2px] tw:text-[12px] tw:leading-5 tw:tracking-[0.6px] tw:text-text-main';

  /** 条件タグ dismiss ボタンの CSS クラス */
  private static readonly TAG_DISMISS_CLASSES = 'tw:text-text-main tw:leading-none tw:bg-transparent tw:border-0 tw:p-0 tw:appearance-none tw:cursor-pointer tw:text-[12px]';

  // 現在の price range 絞り込み状態。null = フィルタなし。
  // DOM 読み取りではなく state で管理することで apply 時の DOM 取得タイミング問題を回避する。
  // maxExplicit: ユーザーがテキスト入力で max を明示指定したか（スライダーデフォルトと区別）
  private _priceRange: { min: number; max: number; maxExplicit: boolean } | null = null;

  // --- bound ハンドラ（kd-receipt-modal パターン準拠） ---

  private boundHandleTriggerClick: (() => void) | null = null;

  private boundHandleCloseClick: (() => void) | null = null;

  private boundHandleApplyClick: ((e: Event) => void) | null = null;

  private boundHandleModalClick: ((e: Event) => void) | null = null;

  private boundHandleAccordionClick: ((e: Event) => void) | null = null;

  private boundHandlePriceInput: ((e: Event) => void) | null = null;

  private boundHandleCheckboxChange: ((e: Event) => void) | null = null;

  private boundHandleClearClick: ((e: Event) => void) | null = null;

  private boundHandleTagDismiss: ((e: Event) => void) | null = null;

  private boundHandleKeydown: ((e: KeyboardEvent) => void) | null = null;

  // --- DOM 参照 ---
  private modal: HTMLElement | null = null;

  connectedCallback(): void {
    super.connectedCallback();

    // ハンドラのバインド
    this.boundHandleTriggerClick = this.openModal.bind(this);
    this.boundHandleCloseClick = this.closeModal.bind(this);
    this.boundHandleApplyClick = this.handleApplyClick.bind(this);
    this.boundHandleModalClick = this.handleModalClick.bind(this);
    this.boundHandleAccordionClick = this.handleAccordionClick.bind(this);
    this.boundHandlePriceInput = this.handlePriceInput.bind(this);
    this.boundHandleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.boundHandleClearClick = this.handleClearClick.bind(this);
    this.boundHandleTagDismiss = this.handleTagDismiss.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);

    // DOM 要素の取得と参照キャッシュ
    this.modal = this.querySelector<HTMLElement>(SELECTORS.COLLECTION.FILTER_MODAL);

    // トリガーボタンは kd-collection-filter の外側にある（document から取得）
    const trigger = document.querySelector(SELECTORS.COLLECTION.FILTER_TRIGGER);
    if (trigger) {
      trigger.addEventListener('click', this.boundHandleTriggerClick);
    }

    // モーダル内ボタン
    const closeBtn = this.querySelector(SELECTORS.COLLECTION.FILTER_MODAL_CLOSE);
    if (closeBtn) {
      closeBtn.addEventListener('click', this.boundHandleCloseClick);
    }

    // PC サイドバー / SP モーダル両方に apply ボタンがあるので querySelectorAll
    const applyBtns = this.querySelectorAll(SELECTORS.COLLECTION.FILTER_APPLY);
    applyBtns.forEach((btn) => {
      btn.addEventListener('click', this.boundHandleApplyClick!);
    });

    // モーダル背景クリック（バックドロップ閉じ）
    if (this.modal) {
      this.modal.addEventListener('click', this.boundHandleModalClick);
    }

    // アコーディオン
    const summaries = this.querySelectorAll(SELECTORS.COLLECTION.FILTER_ACCORDION_SUMMARY);
    summaries.forEach((summary) => {
      summary.addEventListener('click', this.boundHandleAccordionClick);
    });

    // 価格スライダー / 入力フィールド (双方向同期)
    // PC/SP の両方のコンテナに同じ data-* 属性を持つ要素があるため querySelectorAll で全件登録。
    // handlePriceInput 側で event.target の closest コンテナを基準に同期相手を解決する。
    this.getAllPriceInputs().forEach((inp) => {
      inp.addEventListener('input', this.boundHandlePriceInput!);
    });
    // 初期表示: 各コンテナごとに orange バーを slider 値に合わせる
    this.querySelectorAll<HTMLElement>('[data-collection-facets], [data-collection-filter-modal]')
      .forEach((container) => {
        const minSlider = container.querySelector<HTMLInputElement>(SELECTORS.COLLECTION.FILTER_PRICE_MIN);
        const maxSlider = container.querySelector<HTMLInputElement>(SELECTORS.COLLECTION.FILTER_PRICE_MAX);
        if (minSlider && maxSlider) {
          this.updatePriceRangeBar(container, minSlider, maxSlider);
        }
      });

    // チェックボックス変更（イベント委任: this 全体で capture）
    this.addEventListener('change', this.boundHandleCheckboxChange);

    // クリアボタン（PC/SP 複数あるため querySelectorAll）
    const clearBtns = this.querySelectorAll(SELECTORS.COLLECTION.FILTER_CLEAR);
    clearBtns.forEach((btn) => {
      btn.addEventListener('click', this.boundHandleClearClick);
    });

    // ESC キーでモーダルを閉じる
    document.addEventListener('keydown', this.boundHandleKeydown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    // モーダルが開いている場合はスクロールロックを解除
    this.closeModal();

    // トリガーボタン（document から取得）
    if (this.boundHandleTriggerClick) {
      const trigger = document.querySelector(SELECTORS.COLLECTION.FILTER_TRIGGER);
      if (trigger) {
        trigger.removeEventListener('click', this.boundHandleTriggerClick);
      }
    }

    // モーダル内ボタン
    if (this.boundHandleCloseClick) {
      const closeBtn = this.querySelector(SELECTORS.COLLECTION.FILTER_MODAL_CLOSE);
      if (closeBtn) {
        closeBtn.removeEventListener('click', this.boundHandleCloseClick);
      }
    }

    if (this.boundHandleApplyClick) {
      const applyBtns = this.querySelectorAll(SELECTORS.COLLECTION.FILTER_APPLY);
      applyBtns.forEach((btn) => {
        btn.removeEventListener('click', this.boundHandleApplyClick!);
      });
    }

    // モーダル背景クリック
    if (this.modal && this.boundHandleModalClick) {
      this.modal.removeEventListener('click', this.boundHandleModalClick);
    }

    // アコーディオン
    if (this.boundHandleAccordionClick) {
      const summaries = this.querySelectorAll(SELECTORS.COLLECTION.FILTER_ACCORDION_SUMMARY);
      summaries.forEach((summary) => {
        summary.removeEventListener('click', this.boundHandleAccordionClick!);
      });
    }

    // 価格スライダー / 入力フィールド (双方向)
    if (this.boundHandlePriceInput) {
      this.getAllPriceInputs().forEach((inp) => {
        inp.removeEventListener('input', this.boundHandlePriceInput!);
      });
    }

    // チェックボックス変更
    if (this.boundHandleCheckboxChange) {
      this.removeEventListener('change', this.boundHandleCheckboxChange);
    }

    // クリアボタン
    if (this.boundHandleClearClick) {
      const clearBtns = this.querySelectorAll(SELECTORS.COLLECTION.FILTER_CLEAR);
      clearBtns.forEach((btn) => {
        btn.removeEventListener('click', this.boundHandleClearClick!);
      });
    }

    // ESC キー
    if (this.boundHandleKeydown) {
      document.removeEventListener('keydown', this.boundHandleKeydown);
    }

    // 参照クリア
    this.boundHandleTriggerClick = null;
    this.boundHandleCloseClick = null;
    this.boundHandleApplyClick = null;
    this.boundHandleModalClick = null;
    this.boundHandleAccordionClick = null;
    this.boundHandlePriceInput = null;
    this.boundHandleCheckboxChange = null;
    this.boundHandleClearClick = null;
    this.boundHandleTagDismiss = null;
    this.boundHandleKeydown = null;
    this.modal = null;
    this.previouslyFocused = null;
  }

  // --- モーダル開閉 ---

  /** モーダルを開く前のフォーカス元を記憶（閉じた時に復帰するため） */
  private previouslyFocused: HTMLElement | null = null;

  /**
   * モーダル開閉時に flex / flex-col を動的に付与する。
   * 初期 markup に tw:flex を含めると hidden (display:none) と display 競合し、
   * lit-core.css が base.css より後段で読まれるため tw:flex が常に勝ってしまうため。
   */
  private static readonly MODAL_FLEX_CLASSES = ['tw:flex', 'tw:flex-col'];

  private openModal(): void {
    if (!this.modal) return;

    // フォーカス復帰用に現在のフォーカス元を記憶
    this.previouslyFocused = document.activeElement as HTMLElement | null;

    this.modal.classList.remove(STATE_CLASSES.HIDDEN);
    this.modal.classList.add(...KdCollectionFilter.MODAL_FLEX_CLASSES);
    document.body.classList.add(STATE_CLASSES.OVERFLOW_HIDDEN);

    // モーダル内の最初のフォーカス可能要素にフォーカスを移動
    const firstFocusable = this.modal.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  private closeModal(): void {
    if (!this.modal) return;
    this.modal.classList.add(STATE_CLASSES.HIDDEN);
    this.modal.classList.remove(...KdCollectionFilter.MODAL_FLEX_CLASSES);
    document.body.classList.remove(STATE_CLASSES.OVERFLOW_HIDDEN);

    // フォーカスを元の要素に復帰
    if (this.previouslyFocused) {
      this.previouslyFocused.focus();
      this.previouslyFocused = null;
    }
  }

  /** ESC キーでモーダルを閉じる */
  private handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.modal && !this.modal.classList.contains(STATE_CLASSES.HIDDEN)) {
      this.closeModal();
    }
  }

  /** バックドロップクリックでモーダルを閉じる（子要素のクリックは無視） */
  private handleModalClick(e: Event): void {
    if (e.target === e.currentTarget) {
      this.closeModal();
    }
  }

  // --- アコーディオン（kd-order-accordion パターン準拠） ---

  private handleAccordionClick(e: Event): void {
    const summary = (e.currentTarget as HTMLElement);
    const accordion = summary.closest<HTMLElement>(SELECTORS.COLLECTION.FILTER_ACCORDION);
    if (!accordion) return;

    const content = accordion.querySelector<HTMLElement>(SELECTORS.COLLECTION.FILTER_ACCORDION_CONTENT);
    const toggle = accordion.querySelector(SELECTORS.COLLECTION.FILTER_ACCORDION_TOGGLE);
    if (!content || !toggle) return;

    const isOpen = accordion.dataset.open === 'true';

    if (isOpen) {
      this.closeAccordion(accordion, content, toggle);
    } else {
      this.openAccordion(accordion, content, toggle);
    }
  }

  private closeAccordion(accordion: HTMLElement, content: HTMLElement, toggle: Element): void {
    const animation = content.animate(
      { opacity: [1, 0], height: [content.offsetHeight + 'px', '0px'] },
      { duration: KdCollectionFilter.ACCORDION_DURATION, easing: 'ease-out' },
    );

    animation.onfinish = () => {
      accordion.dataset.open = 'false';
      content.classList.add(STATE_CLASSES.HIDDEN);
      toggle.textContent = '+';
      accordion
        .querySelector(SELECTORS.COLLECTION.FILTER_ACCORDION_SUMMARY)
        ?.setAttribute('aria-expanded', 'false');
    };
  }

  private openAccordion(accordion: HTMLElement, content: HTMLElement, toggle: Element): void {
    accordion.dataset.open = 'true';
    content.classList.remove(STATE_CLASSES.HIDDEN);
    toggle.textContent = '\u2212'; // − (マイナス記号)

    accordion
      .querySelector(SELECTORS.COLLECTION.FILTER_ACCORDION_SUMMARY)
      ?.setAttribute('aria-expanded', 'true');

    content.animate(
      { opacity: [0, 1], height: ['0px', content.offsetHeight + 'px'] },
      { duration: KdCollectionFilter.ACCORDION_DURATION, easing: 'ease-out' },
    );
  }

  // --- 条件タグ管理 ---

  /**
   * チェックボックスの変更を検知。
   *
   * ・apply ボタン (data-collection-filter-apply) が同じコンテナにある場合は何もしない。
   *   タグは「この条件で探す」押下時にまとめて再構築する (search ページ PC / SP モーダル)。
   * ・apply ボタンが無いコンテナ (v2-collection-main の PC サイドバー等) は即時にタグを更新する。
   *   v2 検索ページの apply 方式導入後も既存コレクションページの挙動を維持するため。
   */
  private handleCheckboxChange(e: Event): void {
    const target = e.target as HTMLElement;
    if (!target.matches?.(SELECTORS.COLLECTION.FILTER_CHECKBOX)) return;

    // 価格 preset は通常の複数選択チェックボックスとして扱う（案A / 課題#16）。
    // 各 preset は data-algolia-filter（price 式）と filter_group='price' を持ち、
    // dispatchFilterApply が同 group で OR 結合する。slider（自由入力 = _priceRange）とは
    // 独立に共存し、こちらも group='price' で OR 追加される（二重カウントなし: preset は自前 expr、
    // slider は _priceRange で別 filter）。price preset は radio-group を持たないため、
    // 直下のラジオ解除ロジックは素通りする。

    // ラジオグループ: チェック時に全コンテナの同グループ checkbox を解除（PC/SP 同期）
    const cb = target as HTMLInputElement;
    if (cb.checked && cb.dataset.algoliaRadioGroup) {
      const group = cb.dataset.algoliaRadioGroup;
      this.querySelectorAll<HTMLInputElement>(
        `input[data-algolia-radio-group="${escapeAttr(group)}"]`,
      ).forEach((other) => { if (other !== cb) other.checked = false; });
    }

    // 課題#68: 大カテゴリと中カテゴリの同時選択を作らせない。
    // 同一 OR グループに lvl0 と lvl1 が並ぶと lvl0 に吸収され中カテゴリで絞れないため、
    // 起票者確認（2026-08-21）で採択した「大カテゴリ側のチェックを自動で外す」を実装する。
    // twin sync より前に置く（外した側は PC/SP 両方を直接落とすため twin 同期不要）。
    if (cb.checked) this.uncheckExclusiveCategories(cb);

    // PC サイドバーと SP モーダルの双子 checkbox（data-filter-id が <id> と sp-<id>）を
    // 同じ checked 状態に鏡写しする。片側だけ変えると apply 時の whole-component :checked
    // 収集で逆側が残り「フィルタが外せない」主導線バグになる（H1）。ラジオ解除後に呼ぶことで
    // ラジオの twin も正しく checked に揃う。
    this.syncTwinCheckbox(cb);

    const container = target.closest<HTMLElement>('[data-collection-facets], [data-collection-filter-modal]');
    if (!container) return;

    // 同コンテナ内に apply ボタンがあれば pending（押下時に rebuild）
    const hasApplyBtn = container.querySelector(SELECTORS.COLLECTION.FILTER_APPLY) !== null;
    if (hasApplyBtn) return;

    // apply ボタンなし → 即時反映。twin 同期済みのため両コンテナのタグ/バッジを再構築する。
    this.rebuildAllConditionsFromChecked();
  }

  /**
   * 課題#68: いまチェックした大/中カテゴリと排他になる checkbox のチェックを外す。
   * 子を選んだらその親、親を選んだらその配下の子（いずれも PC/SP 両方）。
   * 別の大カテゴリには触らない（「食品」＋「お菓子 > チョコレート」は両方出るのが正）。
   */
  private uncheckExclusiveCategories(cb: HTMLInputElement): void {
    const id = cb.dataset.filterId;
    if (!id) return;
    const baseId = id.replace(/^sp-/, '');
    const allBaseIds = Array.from(
      this.querySelectorAll<HTMLInputElement>('input[data-filter-id]'),
    ).map((el) => (el.dataset.filterId ?? '').replace(/^sp-/, ''));

    getExclusiveCategoryIds(baseId, allBaseIds).forEach((targetId) => {
      this.querySelectorAll<HTMLInputElement>(
        `input[data-filter-id="${escapeAttr(targetId)}"], input[data-filter-id="${escapeAttr('sp-' + targetId)}"]`,
      ).forEach((el) => {
        el.checked = false;
      });
    });
  }

  /**
   * PC サイドバー / SP モーダルの双子 checkbox（data-filter-id が <id> と sp-<id>）の
   * checked 状態を鏡写しにする。単一 <kd-collection-filter> 内に PC/SP の二重 DOM を持つ構造上、
   * apply は whole-component の :checked を収集するため、片側だけ変えると逆側が残る（H1）。
   */
  private syncTwinCheckbox(cb: HTMLInputElement): void {
    const id = cb.dataset.filterId;
    if (!id) return;
    const baseId = id.replace(/^sp-/, '');
    this.querySelectorAll<HTMLInputElement>(
      `input[data-filter-id="${escapeAttr(baseId)}"], input[data-filter-id="${escapeAttr('sp-' + baseId)}"]`,
    ).forEach((twin) => {
      if (twin !== cb && twin.checked !== cb.checked) twin.checked = cb.checked;
    });
  }

  /**
   * 「この条件で探す」適用ボタン: 現在チェック中の checkbox から badges を再構築。
   * SP モーダルの場合は閉じる。
   * 加えて、'kd-collection-filter-apply' CustomEvent を bubble させて Algolia 再検索をトリガする。
   */
  private handleApplyClick(e: Event): void {
    const btn = e.currentTarget as HTMLElement;
    const container = btn.closest('[data-collection-facets], [data-collection-filter-modal]');
    if (!container) return;

    const conditionsEl = container.querySelector<HTMLElement>(SELECTORS.COLLECTION.FILTER_CONDITIONS);
    if (conditionsEl) {
      this.rebuildConditionTags(container as HTMLElement, conditionsEl);
      this.updateConditionsVisibility(conditionsEl);
    }

    // apply 元コンテキストを特定（B ページ初期カテゴリの checked 判定に使用）
    const source: 'pc' | 'sp' = container.matches('[data-collection-filter-modal]') ? 'sp' : 'pc';

    // SP モーダル内なら閉じる
    if (source === 'sp') {
      this.closeModal();
    }

    // Algolia 連携用の CustomEvent を bubble
    this.dispatchFilterApply(source);
  }

  /**
   * 全コンテナ (PC サイドバー + SP モーダル) の :checked から data-algolia-filter を収集し、
   * 重複排除して 'kd-collection-filter-apply' CustomEvent を bubble + composed で配信する。
   * イベントの detail: { filters: Array<{ expr, group }>, source?: 'pc' | 'sp' }。
   *
   * 各 filter エントリは { expr, group } の組:
   * - expr: Algolia filter 式 (例: 'tags:"送料無料"', 'price <= 1000')
   * - group: OR 結合用キー。data-algolia-filter-group があればそれ、無ければ
   *          式の先頭から推定 (NOT 接頭辞を除いて : / < / = 前まで)。
   *          同 group は OR、group 間は AND で結合される (refinementList 相当)。
   *
   * source は apply ボタンを押したコンテナ（pc/sp）。B ページの初期カテゴリ解除判定に使用。
   * タグ ✕ / クリアボタン経由の場合は undefined（checkbox が既に解除済みのため判定不要）。
   *
   * 「この条件で探す」適用 / 個別タグ ✕ / 「条件をクリア」 のいずれでも、状態変化後に
   * 同じイベントを bubble させて再検索を統一トリガする。
   */
  private dispatchFilterApply(source?: 'pc' | 'sp'): void {
    const filters: Array<{ expr: string; group: string }> = [];
    const seen = new Set<string>();
    this.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"][data-algolia-filter]:checked:not(:disabled)',
    ).forEach((cb) => {
      const expr = cb.dataset.algoliaFilter;
      if (!expr || seen.has(expr)) return;
      seen.add(expr);
      const explicit = cb.dataset.algoliaFilterGroup;
      const group =
        explicit ?? expr.replace(/^NOT\s+/, '').split(/[:<>=]/)[0].trim();
      filters.push({ expr, group });
    });

    // 価格レンジフィルタ: rangeInput の値から生成（checkbox filter_expr とは独立）
    const priceFilter = this._buildPriceRangeFilter();
    if (priceFilter) filters.push(priceFilter);

    this.dispatchEvent(
      new CustomEvent('kd-collection-filter-apply', {
        detail: { filters, source },
        bubbles: true,
        composed: true,
      }),
    );

    // SP トリガーの適用中フィルタ数バッジを更新（適用 / タグ✕ / クリアの全経路がここを通る）
    this.updateFilterCountBadge();
  }

  /**
   * _priceRange state から Algolia price filter エントリを生成する。
   * _priceRange は rangeInput 直接編集時のみ更新される（自由入力専用）。
   * price preset は data-algolia-filter で直接フィルタを送るため _priceRange は不使用。
   */
  private _buildPriceRangeFilter(): { expr: string; group: string } | null {
    if (!this._priceRange) return null;
    const expr = buildPriceRangeFilterExpr(this._priceRange.min, this._priceRange.max, this._priceRange.maxExplicit);
    return expr ? { expr, group: 'price' } : null;
  }

  /**
   * 全コンテナ (PC サイドバー + SP モーダル) の :checked 状態から条件タグを再構築する。
   * URL ハイドレーション後など、外部からチェック状態を変えたあとに呼んで
   * 条件タグ表示を最新化するための public API。
   */
  public rebuildAllConditionsFromChecked(): void {
    this.querySelectorAll<HTMLElement>(
      '[data-collection-facets], [data-collection-filter-modal]',
    ).forEach((container) => {
      const conditionsEl = container.querySelector<HTMLElement>(SELECTORS.COLLECTION.FILTER_CONDITIONS);
      if (conditionsEl) {
        this.rebuildConditionTags(container, conditionsEl);
        this.updateConditionsVisibility(conditionsEl);
      }
    });
    // URL ハイドレーション後にバッジも最新化
    this.updateFilterCountBadge();
  }

  /** 条件タグを現在のチェック状態から再構築する（apply 時に呼ぶ） */
  private rebuildConditionTags(container: HTMLElement, conditionsEl: HTMLElement): void {
    // 既存 dismiss リスナーを解除して中身をクリア
    const existingBtns = conditionsEl.querySelectorAll('button');
    existingBtns.forEach((b) => {
      if (this.boundHandleTagDismiss) b.removeEventListener('click', this.boundHandleTagDismiss);
    });
    conditionsEl.replaceChildren();

    // 同じコンテナ内の checked checkbox を走査して badges 追加
    const checked = container.querySelectorAll<HTMLInputElement>(`${SELECTORS.COLLECTION.FILTER_CHECKBOX}:checked`);
    checked.forEach((cb) => {
      const filterId = cb.dataset.filterId;
      if (!filterId) return;
      const label = cb.closest('label');
      // [data-filter-label] があれば件数 (（108） 等) を含めずラベル名だけ取得。
      // 無い場合は label.textContent にフォールバック (legacy v2-collection-main 互換)。
      const labelEl = label?.querySelector<HTMLElement>('[data-filter-label]');
      const labelText = labelEl?.textContent?.trim() ?? label?.textContent?.trim() ?? '';
      this.addConditionTag(conditionsEl, filterId, labelText);
    });
  }

  /** 条件タグを作成してコンテナに追加 */
  private addConditionTag(container: HTMLElement, filterId: string, labelText: string): void {
    const tag = document.createElement('span');
    tag.setAttribute('data-filter-tag', filterId);
    tag.className = KdCollectionFilter.TAG_CLASSES;

    const text = document.createTextNode(labelText);
    tag.appendChild(text);

    const dismissBtn = document.createElement('button');
    dismissBtn.type = 'button';
    dismissBtn.className = KdCollectionFilter.TAG_DISMISS_CLASSES;
    dismissBtn.setAttribute('aria-label', `${labelText} を解除`);
    dismissBtn.textContent = '✕';
    dismissBtn.addEventListener('click', this.boundHandleTagDismiss!);
    tag.appendChild(dismissBtn);

    container.appendChild(tag);
  }

  /** 条件タグの dismiss ボタンクリックでチェックボックスを解除 */
  private handleTagDismiss(e: Event): void {
    const btn = (e.currentTarget as HTMLElement);
    const tag = btn.closest<HTMLElement>('[data-filter-tag]');
    if (!tag) return;

    const filterId = tag.dataset.filterTag;
    if (!filterId) return;

    // PC・SP 双子のチェックボックスを両方解除する。SP タグは data-filter-tag が sp-<id> のため、
    // そのまま "sp-" を前置すると sp-sp-<id>（存在しない）になり PC 側 <id> も外せずフィルタが残る（H2）。
    // 先頭 sp- を除去した baseId から <id> と sp-<id> の両バリアントを解決する。
    const baseId = filterId.replace(/^sp-/, '');
    this.querySelectorAll<HTMLInputElement>(
      `input[data-filter-id="${escapeAttr(baseId)}"], input[data-filter-id="${escapeAttr('sp-' + baseId)}"]`,
    ).forEach((cb) => { cb.checked = false; });

    // 押下側だけでなく twin コンテナのタグも消すため :checked から両コンテナ再構築する
    // （dismiss リスナー解放も rebuildConditionTags 内で実施）。
    this.rebuildAllConditionsFromChecked();

    // タグ ✕ で 1 件解除 → 残りの :checked で再検索
    this.dispatchFilterApply();
  }

  /** 条件コンテナの表示/非表示を更新 */
  private updateConditionsVisibility(container: HTMLElement): void {
    const hasTags = container.querySelectorAll('[data-filter-tag]').length > 0;
    if (hasTags) {
      container.classList.remove(STATE_CLASSES.HIDDEN);
      // v1 prefix-less 'flex' は base.css に未定義のため display:block のままになっていた。
      // v2 の `tw:flex` を追加して tw:flex-wrap / tw:gap-2 を有効化する。
      // 旧 v2-collection-main 等で 'flex' クラスを期待する箇所との互換性のため両方付ける。
      container.classList.add('tw:flex', 'flex');
    } else {
      container.classList.add(STATE_CLASSES.HIDDEN);
      container.classList.remove('tw:flex', 'flex');
    }

    // クリアボタンの表示制御（PC サイドバー + SP モーダル両対応）。
    // 課題#16: 旧実装は [data-collection-facets]（PC）のみを対象にしており、
    // SP モーダル（[data-collection-filter-modal]）配下の「条件をクリア」は
    // markup 上存在するのに hidden が外れず「SP にボタンがない」状態だった。
    // 他処理（apply/clear/price 同期）と同じ両コンテナスコープに揃える。
    const scope = container.closest('[data-collection-facets], [data-collection-filter-modal]');
    if (scope) {
      const clearBtn = scope.querySelector<HTMLElement>(SELECTORS.COLLECTION.FILTER_CLEAR);
      if (clearBtn) {
        if (hasTags) {
          clearBtn.classList.remove(STATE_CLASSES.HIDDEN);
        } else {
          clearBtn.classList.add(STATE_CLASSES.HIDDEN);
        }
      }
    }
  }

  /**
   * SP 絞り込みトリガーの「適用中フィルタ数」バッジを現在の :checked 状態から更新する。
   * 数え方は dispatchFilterApply が送る filters 集合と同じ（expr で重複排除 + 価格レンジ）。
   * 件数 0 のときは hidden 属性を戻す。適用 / タグ✕ / クリア（dispatchFilterApply 経由）と
   * URL ハイドレーション（rebuildAllConditionsFromChecked）から呼ばれる。
   */
  private updateFilterCountBadge(): void {
    const badge = this.querySelector<HTMLElement>(SELECTORS.COLLECTION.FILTER_COUNT_BADGE);
    if (!badge) return;

    const exprs: string[] = [];
    this.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"][data-algolia-filter]:checked:not(:disabled)',
    ).forEach((cb) => {
      if (cb.dataset.algoliaFilter) exprs.push(cb.dataset.algoliaFilter);
    });

    const count = countActiveFilters(exprs, this._priceRange !== null);
    if (count > 0) {
      badge.textContent = String(count);
      badge.classList.remove(STATE_CLASSES.HIDDEN);
      badge.classList.add('tw:inline-flex');
    } else {
      badge.textContent = '';
      badge.classList.add(STATE_CLASSES.HIDDEN);
      badge.classList.remove('tw:inline-flex');
    }
  }

  /** クリアボタン: 全チェックボックスを解除、全タグを削除 */
  private handleClearClick(e: Event): void {
    const btn = e.currentTarget as HTMLElement;

    // クリアボタンの所属コンテナ存在ガード（解除自体は下で this 全体に適用する）
    const container = btn.closest('[data-collection-facets], [data-collection-filter-modal]');
    if (!container) return;

    // 全チェックボックスを解除（押下側コンテナだけでなく PC/SP 双方 = this 全体）。
    // 押下側だけ解除すると逆側 checkbox が残り apply の whole-component :checked 収集で拾われ、
    // 「条件をクリア」が実質無効になる主導線バグになる（H1）。タグは後段の
    // rebuildAllConditionsFromChecked() が :checked 空を反映して両コンテナ分まとめて消す。
    this.querySelectorAll<HTMLInputElement>(SELECTORS.COLLECTION.FILTER_CHECKBOX).forEach((cb) => {
      cb.checked = false;
    });

    // 価格レンジ state をリセット
    this._priceRange = null;

    // 価格レンジ UI をデフォルトにリセット。
    // slider 値の直接代入（.value=）は input イベントを発火しないため handlePriceInput は走らず、
    // 中間状態フィルタも生成されない（旧 _pricePresetUpdating ガードは到達不能だったため撤去）。
    this.querySelectorAll<HTMLElement>(
      '[data-collection-facets], [data-collection-filter-modal]',
    ).forEach((priceContainer) => {
      const minSlider = priceContainer.querySelector<HTMLInputElement>(SELECTORS.COLLECTION.FILTER_PRICE_MIN);
      const maxSlider = priceContainer.querySelector<HTMLInputElement>(SELECTORS.COLLECTION.FILTER_PRICE_MAX);
      if (minSlider) minSlider.value = minSlider.min;
      if (maxSlider) maxSlider.value = maxSlider.max;
      this.syncLabel(priceContainer, SELECTORS.COLLECTION.FILTER_PRICE_MIN_LABEL, '');
      this.syncLabel(priceContainer, SELECTORS.COLLECTION.FILTER_PRICE_MAX_LABEL, '');
      if (minSlider && maxSlider) this.updatePriceRangeBar(priceContainer, minSlider, maxSlider);
    });

    // 両コンテナのタグ/バッジを :checked（=空）から再構築＝全タグ消去 + リスナー解放。
    this.rebuildAllConditionsFromChecked();

    // 「条件をクリア」→ filters 空で再検索 (フィルタ無し状態に戻す)
    this.dispatchFilterApply();
  }

  // --- 価格スライダー（ラベル更新 + 選択範囲オレンジ表示同期） ---

  /**
   * スライダー / 入力フィールドが変化したときに、対応する片側を同期し、
   * 選択範囲（オレンジバー）の left/right を thumb 位置に追従させる。
   *
   * - 入力先がスライダー側 (`type=range`) なら、対になる input[type=text] に値を反映
   * - 入力先がテキスト入力 (`data-price-min/max-label`) なら、対になるスライダーに値を反映
   * - data-price-range の left/right を min/max ÷ slider 範囲 で更新
   */
  private handlePriceInput(e: Event): void {
    const input = e.target as HTMLInputElement;

    // PC サイドバーと SP モーダルで同じ data-* 属性が並列存在するため、
    // 入力元のコンテナにスコープしないと反対側の DOM を誤って更新してしまう。
    const container = input.closest<HTMLElement>(
      '[data-collection-facets], [data-collection-filter-modal]',
    );
    if (!container) return;

    // 入力されたのが range スライダーかテキスト入力か判定
    const isMinSlider = input.matches(SELECTORS.COLLECTION.FILTER_PRICE_MIN);
    const isMaxSlider = input.matches(SELECTORS.COLLECTION.FILTER_PRICE_MAX);
    const isMinLabel = input.matches(SELECTORS.COLLECTION.FILTER_PRICE_MIN_LABEL);
    const isMaxLabel = input.matches(SELECTORS.COLLECTION.FILTER_PRICE_MAX_LABEL);

    // 同コンテナ内のスライダー参照（PC サイドバーのようにスライダーが無いケースもある）
    const minSlider = container.querySelector<HTMLInputElement>(SELECTORS.COLLECTION.FILTER_PRICE_MIN);
    const maxSlider = container.querySelector<HTMLInputElement>(SELECTORS.COLLECTION.FILTER_PRICE_MAX);

    // 双方向同期: スライダー → ラベル / ラベル → スライダー
    if (isMinSlider && minSlider && maxSlider) {
      // min thumb は max thumb を超えないようクランプ
      if (Number(minSlider.value) > Number(maxSlider.value)) {
        minSlider.value = maxSlider.value;
      }
      this.syncLabel(container, SELECTORS.COLLECTION.FILTER_PRICE_MIN_LABEL, minSlider.value);
    } else if (isMaxSlider && minSlider && maxSlider) {
      if (Number(maxSlider.value) < Number(minSlider.value)) {
        maxSlider.value = minSlider.value;
      }
      // 右端（= UI 上限）ではラベルをクリアして「上限なし」を明示
      const maxAtEnd = maxSlider.value === maxSlider.max;
      this.syncLabel(container, SELECTORS.COLLECTION.FILTER_PRICE_MAX_LABEL, maxAtEnd ? '' : maxSlider.value);
    } else if (isMinLabel) {
      const v = this.parseLabelValue(input.value);
      if (v !== null && minSlider && maxSlider) {
        minSlider.value = String(Math.min(v, Number(maxSlider.value)));
      }
    } else if (isMaxLabel) {
      input.dataset.userInput = 'true';
      const v = this.parseLabelValue(input.value);
      if (v !== null && minSlider && maxSlider) {
        maxSlider.value = String(Math.max(v, Number(minSlider.value)));
      }
    }

    // 選択範囲オレンジバーを thumb 位置に追従（スライダーが両方ある時のみ）
    if (minSlider && maxSlider) {
      this.updatePriceRangeBar(container, minSlider, maxSlider);
    }

    // ユーザーが直接 range を編集 → state 更新（自由入力レンジ = _priceRange）。
    // 案A（課題#16）: slider と price preset は独立共存するため preset は解除しない。
    // slider レンジは group='price' で preset の各式と OR 結合される。
    // テキスト入力値を優先（スライダーの max="50000" クランプで値が落ちるのを回避）
    const maxLabelEl = container.querySelector<HTMLInputElement>(SELECTORS.COLLECTION.FILTER_PRICE_MAX_LABEL);
    const minLabelValue = container.querySelector<HTMLInputElement>(SELECTORS.COLLECTION.FILTER_PRICE_MIN_LABEL)?.value?.trim();
    const maxLabelValue = maxLabelEl?.value?.trim();
    const maxExplicit = maxLabelEl?.dataset.userInput === 'true';
    const minFallback = minSlider ? Number(minSlider.value) : 0;
    const maxFallback = maxSlider ? Number(maxSlider.value) : PRICE_SLIDER_MAX;
    const currentMin = minLabelValue ? parsePriceInputValue(minLabelValue, 0) : minFallback;
    const currentMax = maxLabelValue ? parsePriceInputValue(maxLabelValue, PRICE_SLIDER_MAX) : maxFallback;
    this._priceRange = buildPriceRangeFilterExpr(currentMin, currentMax, maxExplicit) ? { min: currentMin, max: currentMax, maxExplicit } : null;
  }

  /** PC/SP 両方の price 関連 input/range をまとめて取得 */
  private getAllPriceInputs(): HTMLInputElement[] {
    return Array.from(
      this.querySelectorAll<HTMLInputElement>(
        [
          SELECTORS.COLLECTION.FILTER_PRICE_MIN,
          SELECTORS.COLLECTION.FILTER_PRICE_MAX,
          SELECTORS.COLLECTION.FILTER_PRICE_MIN_LABEL,
          SELECTORS.COLLECTION.FILTER_PRICE_MAX_LABEL,
        ].join(', '),
      ),
    ).filter((el) => el instanceof HTMLInputElement);
  }

  /** ラベル要素 (text input or span) に値を反映 (コンテナ内に限定)。スライダー由来の同期なので user-input フラグをクリアする */
  private syncLabel(container: HTMLElement, selector: string, value: string): void {
    const label = container.querySelector<HTMLElement>(selector);
    if (!label) return;
    // 空文字はリセット扱い: ラベルをクリア（Number('') = 0 で '0' が書き込まれるのを防止）
    const formatted = value ? Number(value).toLocaleString('ja-JP') : '';
    if (label instanceof HTMLInputElement) {
      label.value = formatted;
      delete label.dataset.userInput;
    } else {
      label.textContent = formatted;
    }
  }

  /** "1,000" や "1000円" などをパースして数値化、不正・空なら null */
  private parseLabelValue(raw: string): number | null {
    // 空文字/空白のみは「入力なし」= null。Number('') は 0 で Number.isFinite(0)===true のため
    // 空欄を 0 と誤解し price<=0 級の 0 件フィルタを生成してしまう（M15）ので先に弾く。
    if (!raw.trim()) return null;
    const num = Number(raw.replace(/[^\d.-]/g, ''));
    return Number.isFinite(num) ? num : null;
  }

  /** 選択範囲オレンジバー (data-price-range) の left/right を thumb 位置に追従 (コンテナ内に限定) */
  private updatePriceRangeBar(
    container: HTMLElement,
    minSlider: HTMLInputElement,
    maxSlider: HTMLInputElement,
  ): void {
    const bar = container.querySelector<HTMLElement>(SELECTORS.COLLECTION.FILTER_PRICE_RANGE);
    if (!bar) return;

    const sliderMin = Number(minSlider.min) || 0;
    const sliderMax = Number(maxSlider.max) || 100;
    const span = sliderMax - sliderMin;
    if (span <= 0) return;

    const minPct = ((Number(minSlider.value) - sliderMin) / span) * 100;
    const maxPct = ((Number(maxSlider.value) - sliderMin) / span) * 100;
    bar.style.left = `${minPct}%`;
    bar.style.right = `${100 - maxPct}%`;
  }

  render() {
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-collection-filter': KdCollectionFilter;
  }
}
